import type { ErrorComponentProps } from '@tanstack/react-router';

import { CrashScreen } from '~shared/observability/crash-screen';
import { reportError } from '~shared/observability/report-error';

/**
 * Router-level error component wired into the router via `defaultErrorComponent`.
 *
 * TanStack Router catches errors thrown while rendering a route *before* they
 * reach the top-level Sentry ErrorBoundary, and by default renders its own
 * unbranded crash box that dumps the raw error and is never reported to Sentry.
 * This component replaces that: it reports the crash to Sentry (tagged `route`)
 * and renders the shared, branded CrashScreen.
 */
export const RouteErrorComponent = ({
  error,
  reset,
  info,
}: ErrorComponentProps) => {
  // Capture during render so the first paint already has the Sentry event id
  // (and the notified copy + reference code). reportError is synchronous and
  // dedupes the same Error instance via WeakMap, so remounts / StrictMode are safe.
  const eventId = reportError(error, { featureArea: 'route' });

  return (
    <CrashScreen
      error={error}
      eventId={eventId}
      componentStack={info?.componentStack}
      onRetry={reset}
      description={
        eventId
          ? 'This page ran into an unexpected problem. Our team has been automatically notified. You can try again, reload the page, or head back to the dashboard.'
          : 'This page ran into an unexpected problem. You can try again, reload the page, or head back to the dashboard.'
      }
    />
  );
};
